import { Component, OnInit, Input, OnChanges, SimpleChanges, NgZone, OnDestroy } from '@angular/core';
import { GoogleChartService } from '../services/google-chart.service';
import { Router, ActivatedRoute, RouterEvent, NavigationEnd } from '@angular/router';
import { psegLoadsTbl } from '../Models/LoadsData';
import { DatePipe } from '@angular/common';
import { MinMaxDate } from '../Models/MiscModels';
import { PsegHrlyDemandService } from '../services/pseg-hrly-demand.service';
import { SignalrISOdataService } from '../services/signalr-ISOdata.service';
import { MiscService } from '../services/misc.service';
import { NONE_TYPE } from '@angular/compiler/src/output/output_ast';
import { ResizedEvent } from 'angular-resize-event';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-pseg-load-chart',
  templateUrl: './pseg-load-chart.component.html',
  styleUrls: ['./pseg-load-chart.component.css']
})
export class PsegLoadChartComponent implements OnInit, OnDestroy {
  public destroyed = new Subject<any>();
  private gLib: any;
  private minMaxDate: MinMaxDate;

  private psegLoadDashboard: any;

  private psegLoadLine: any;
  private psegLoadDateSlider: any;
  public btnLabel = 'Show as Table';
  private psegLoadTable;
  private chartTitle: string;

  constructor(private gChartService: GoogleChartService, private route: ActivatedRoute,
    private signalrService: SignalrISOdataService, private _ngZone: NgZone, private miscSvc: MiscService) {

  }

  private getOptions(chartType) {
    let options;

    switch (chartType) {

      case 'ComboChart':
        options = {
          enableInteractivity: true,
          interpolateNulls: 'false',
          series: {
            0: { type: 'bars', color: '#c8393c', targetAxisIndex: 0 },
            1: { type: 'line', targetAxisIndex: 0, pointSize: 0, color: '#13f9f6', lineWidth: 7, interpolateNulls: 'false' },
            2: {
              type: 'steppedArea', targetAxisIndex: 0,
              color: '#c0c0c0', lineWidth: 2, interpolateNulls: 'true', opacity: 0.01
            },
            3: { type: 'steppedArea', targetAxisIndex: 0, color: 'red', lineWidth: 7, interpolateNulls: 'false', opacity: 0.05 }

          },

          colors: ['#13f9f6'],
          legend: {
            position: 'top', alignment: 'end', textStyle: { fontSize: 14 },
          },
          titleTextStyle: {
            color: 'black',    // any HTML string color ('red', '#cc00cc')
            fontName: 'Montesserat', // i.e. 'Times New Roman'
            fontSize: '22', // 12, 18 whatever you want (don't specify px)
            bold: false,    // true or false
            italic: true,   // true of false
          },
          chartArea: { width: '80%', height: '80%' },
          crosshair: { trigger: 'both' },
          curveType: 'none',

          vAxis:
          {
            title: 'PSEG Zone Load', textStyle: { fontSize: '12' }, format: '#,### MW',
            minValue: 3000, maxValue: 11000, gridlines: { count: 0 }
          },
          //   { title: 'PSEG Zone Load', textStyle: { fontSize: '12' }, format: '#,### MW' }
          //   // , minValue: 2000, maxValue: 10000, gridlines: { count: 8 }

          hAxis: {
            textStyle: {
              fontSize: '12'
            },
            gridlines: {
              count: -1,
              units: {
                days: { format: ['MMM dd'] },
                hours: { format: ['HH:mm', 'ha'] },
              }
            },
            minorGridlines: {
              units: {
                hours: { format: ['hh:mm:ss a', 'ha'] },
                minutes: { format: ['HH:mm a Z', ':mm'] }
              }
            },

          },
        };

        break;

      case 'Table':
        options = {
          showRowNumber: true,
          width: '80%',
          height: '80%'
        };
        break;

      default:
        options = {};
    }

    return options;
  }

  getFilterOptions() {
    const controlOptions = {
      // Filter by the date axis.
      'filterColumnIndex': 0,
      ui: {
        'chartOptions': {
          colors: ['grey'], pointSize: 0, lineWidth: 1,
          chartArea: { width: '80%', height: '100%' },
          hAxis: {
            textStyle: {
              fontSize: '14'
            },
            gridlines: {
              count: -1,
              units: {
                days: { format: ['MMM dd'] },
                hours: { format: ['HH:mm', 'ha'] },
              }
            },
            minorGridlines: {
              units: {
                hours: { format: ['hh:mm:ss a', 'ha'] },
                minutes: { format: ['HH:mm a Z', ':mm'] }
              }
            }
          }
        },
      }
    };
    return controlOptions;
  }

  ngOnInit() {
    this.gLib = this.gChartService.getGoogle();
    this.gLib.charts.load('current', { 'packages': ['corechart', 'table', 'controls'] });
    this.gLib.charts.setOnLoadCallback(this.drawPsegLoadChart.bind(this));
    this.psegLoadTable = new this.gLib.visualization.DataTable();

    this.signalrService.PsegLoadsRefreshMessageReceived
      .pipe(takeUntil(this.destroyed))
      .subscribe((data) => {
        this._ngZone.run(() => {
          this.updateChartDataTable(data);

        });
      });
    }
  formatAxes() {
    const date_formatter = new this.gLib.visualization.DateFormat({ pattern: 'MMM dd, yyyy,  h:mm aa' });
    date_formatter.format(this.psegLoadTable, 0);  // Where 0 is the index of the column

    const y1formatter = new this.gLib.visualization.NumberFormat({ suffix: ' MW', pattern: '#,###' });
    y1formatter.format(this.psegLoadTable, 1); // Apply formatter to first column

    y1formatter.format(this.psegLoadTable, 2); // Apply formatter to second column

    // y2formatter.format(this.psegLoadTable, 3);  Apply formatter to third column
  }

  private createChartDataTable(data: Array<psegLoadsTbl>) {

    this.psegLoadTable.addColumn('date', 'Date');

    this.psegLoadTable.addColumn('number', 'PSE&G Load');

    this.psegLoadTable.addColumn('number', 'PSE&G Short Term Forecast');

    this.psegLoadTable.addColumn({ type: 'string', role: 'style' });

    this.psegLoadTable.addColumn('number', 'Inferred Peaks');
    this.psegLoadTable.addColumn({ type: 'string', role: 'tooltip' });

    this.psegLoadTable.addColumn('number', 'Extended Peaks');
    this.psegLoadTable.addColumn({ type: 'string', role: 'tooltip' });

    const evalDateTime = new Date(data[data.length - 1].evaluatedAt);
    const evalTimeStr = evalDateTime.toLocaleString() + ' (EST)';
    this.chartTitle = 'PSE&G Zone Load at 5 minute intervals. Last Reading at ' + evalTimeStr;

    this.addRowsChartDataTable(data);
  }

  private addRowsChartDataTable(data: Array<psegLoadsTbl>) {
    const chartRow = new Array<Date | number | string | boolean>();

    for (let i = 0; i < data.length; i++) {

      chartRow.length = 0;

      const date = new Date(data[i].timestamp);
      chartRow.push(date);

      // chartRow.push(data[i].consumptionLoad / 1000);
      chartRow.push(data[i].psegLoad);

      chartRow.push(data[i].psVeryShortForecast);

      chartRow.push('opacity:.5');

      const peak = data[i].peak;
      if ((peak !== 0 || peak != null) && (peak === 1)) {
        chartRow.push(11000);
        chartRow.push('Local Peak:  Timestamp ending at ' + date.toString());
      } else {
        chartRow.push(0);
        chartRow.push('Local Peak:  Timestamp ending at ' + date.toString());
      }

      if ((peak !== 0 || peak != null) && (peak === 3)) {
        chartRow.push(11000);
        chartRow.push('Extended Peak:  Timestamp ending at ' + date.toString());
      } else {
        chartRow.push(0);
        chartRow.push(' ');
      }
      this.psegLoadTable.addRow(chartRow);

    }
    this.formatAxes();
  }

  private updateChartDataTable(data: Array<psegLoadsTbl>) {

    if (this.psegLoadDashboard !== undefined) {
      const maxEvalAtStr = this.miscSvc.removeRowsInstLoad(this.psegLoadTable, data);
      this.addRowsChartDataTable(data);
      this.updatePsegLoadChart(data, maxEvalAtStr);
    }
  }




  private createLoadChartWrapper() {   // Line chart visualization

    this.psegLoadLine = new this.gLib.visualization.ChartWrapper({
      'chartType': 'ComboChart',
      'containerId': 'psegLoadLine_div',
    });
    const options = this.getOptions('ComboChart');
    this.psegLoadLine.setOptions(options);
    this.psegLoadLine.setOption('title', this.chartTitle);
  }
  private drawPsegLoadChart(options) {

    this.createChartDataTable(this.route.snapshot.data.PsegLoadData);


    // Create a dashboard.
    const dash_container = document.getElementById('psegLoadDashboard_div');


    this.psegLoadDashboard = new this.gLib.visualization.Dashboard(dash_container);

    const dtNumRows = this.psegLoadTable.getNumberOfRows();

    const MaxDt = new Date(this.psegLoadTable.getValue(dtNumRows - 1, 0));
    const MinDt = new Date(this.psegLoadTable.getValue(0, 0));


    // Create a date range slider
    this.psegLoadDateSlider = new this.gLib.visualization.ControlWrapper({
      'controlType': 'ChartRangeFilter',
      'containerId': 'psegLoadcontrol_div',

    });
    const state_obj = { 'range': { 'start': MinDt, 'end': MaxDt } };
    this.psegLoadDateSlider.setState(state_obj);
    const controlOptions = this.getFilterOptions();
    this.psegLoadDateSlider.setOptions(controlOptions);


    this.createLoadChartWrapper();


    // Bind psegLoadLine to the dashboard, and to the controls
    // this will make sure our line chart is updated when our date changes
    this.psegLoadDashboard.bind(this.psegLoadDateSlider, this.psegLoadLine);

    const chartType = this.psegLoadLine.getChartType();
    if (chartType === 'Table') {
      const view = new this.gLib.visualization.DataView(this.psegLoadTable);
      // view.hideColumns([4, 5]);
      this.psegLoadDashboard.draw(view, { sortColumn: 0 });
    } else {
      this.psegLoadDashboard.draw(this.psegLoadTable);
    }
  }

  private updatePsegLoadChart(data: Array<psegLoadsTbl>, maxEvalTimeStr: String) {

    if (this.psegLoadDashboard !== undefined) {
      this.chartTitle = 'PSE&G Zone Load at 5 minute intervals. Last Reading at ' + maxEvalTimeStr;
      this.psegLoadLine.setOption('title', this.chartTitle);

      let state_obj = this.psegLoadDateSlider.getState();

      const startDt: Date = new Date(state_obj.range.start);
      const endDt: Date = new Date(data[data.length - 1].timestamp);

       state_obj = { 'range': { 'start': startDt, 'end': endDt } };
       this.psegLoadDateSlider.setState(state_obj);
       const chartType = this.psegLoadLine.getChartType();
      if (chartType === 'Table') {
        const view = new this.gLib.visualization.DataView(this.psegLoadTable);
        // view.hideColumns([4, 5]);
        this.psegLoadDashboard.draw(view, { sortColumn: 0 });
      } else {
        this.psegLoadDashboard.draw(this.psegLoadTable);
      }

    }
  }

  onResized(event: ResizedEvent) {
    const width = event.newWidth;
    const height = event.newHeight;

    if (this.psegLoadLine !== undefined) {
      this.psegLoadLine.draw();
      this.psegLoadDateSlider.draw();
    }
  }
  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }
  private switchChart() {

    let chartType;
    const view = new this.gLib.visualization.DataView(this.psegLoadTable);

    view.hideColumns([4, 5]);
    // tslint:disable-next-line:triple-equals
    if (this.btnLabel == 'Show as Chart') {
      chartType = 'ComboChart';
      this.psegLoadLine.setChartType(chartType);
      this.btnLabel = 'Show as Table';
    } else {
      chartType = 'Table';
      this.psegLoadLine.setChartType(chartType);
      this.btnLabel = 'Show as Chart';

    }
    const ChartType = this.psegLoadLine.getChartType();

    const options = this.getOptions(chartType);
    this.psegLoadLine.setOptions(options);
    this.psegLoadLine.setOption('title', this.chartTitle);
    if (chartType === 'Table') {
      this.psegLoadDashboard.draw(view, { sortColumn: 0 });
    } else {
      this.psegLoadDashboard.draw(this.psegLoadTable);
    }
  }

}

